import { Component, OnInit } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { PassengerNameRecord } from '@fcom/common-booking';
import { isPresent } from '@fcom/core/utils/utils';
import { filterWithoutContentType, filterWithoutType, findByType } from '@fcom/cms';
import { CmsPageService } from '@fcom/common';
import { CmsCollection, CmsContentType, CmsImageData, CmsTemplate, CmsViewType } from '@fcom/core-api';
import { RootPaths } from '@fcom/core/constants';
import { clearErrors } from '@fcom/one-upgrade/store/actions/status.actions';

import { BookingRef, Stage, State, UpgradeError } from '../../interfaces';
import { AuthActions, getBookingRef, getError, getStage, StatusActions } from '../../store';

@Component({
  selector: 'fin-upgrade-login',
  templateUrl: 'login.component.html',
})
export class LoginComponent implements OnInit {
  imageData$: Observable<CmsImageData>;
  loading$: Observable<boolean>;
  mainError$: Observable<string>;
  template$: Observable<CmsTemplate>;
  filteredTemplate$: Observable<CmsTemplate>;
  disruptions$: Observable<CmsCollection>;
  personalized$: Observable<CmsCollection>;
  currentStage$: Observable<Stage>;
  prefillableBookingRef$: Observable<BookingRef>;

  subscription = new Subscription();

  readonly UpgradeError = UpgradeError;
  readonly Stage = Stage;

  constructor(
    private store: Store<State>,
    private pageService: CmsPageService
  ) {}

  ngOnInit(): void {
    this.template$ = this.pageService.getPageDataAndSetMeta(RootPaths.ONE_UPGRADE_ROOT);
    this.currentStage$ = this.store.pipe(select(getStage));
    this.filteredTemplate$ = this.template$.pipe(
      filterWithoutType(CmsViewType.DISRUPTIONS, 'main'),
      filterWithoutContentType(CmsContentType.CMPersonalized, 'main')
    );
    this.disruptions$ = this.template$.pipe(
      map((template) => template.main),
      findByType<CmsCollection>(CmsViewType.DISRUPTIONS)
    );

    this.personalized$ = this.template$.pipe(
      map((template) => {
        return template.main.filter((item) => item.contentType === CmsContentType.CMPersonalized);
      }),
      filter((item) => !!item?.length),
      map((personalizedContent) => {
        return personalizedContent[0].items?.[0] as CmsCollection;
      })
    );

    this.mainError$ = this.store.pipe(
      select(getError),
      filter((err) => [UpgradeError.httpError, UpgradeError.none].includes(err)),
      map((err) => (err === UpgradeError.httpError ? 'oneUpgrade.login.notFound' : null))
    );

    /* If user has typoed their recLoc or lastname, we have an http error and the previously typoed info
     * that we can use to prefill the pnr widget */
    this.prefillableBookingRef$ = combineLatest([this.store.pipe(select(getBookingRef)), this.mainError$]).pipe(
      filter(([bookingRef, mainError]) => {
        return isPresent(bookingRef) && isPresent(mainError);
      }),
      take(1),
      map(([bookingRef, _mainError]) => bookingRef)
    );

    this.imageData$ = this.template$.pipe(
      map((content) => content.main.find((obj) => obj.contentType === CmsContentType.CMPlaceholder)),
      filter(Boolean),
      map((data) => data.picture)
    );
  }

  closeErrorNotification(): void {
    this.store.dispatch(StatusActions.clearErrors());
  }

  onSubmit(event: PassengerNameRecord): void {
    this.store.dispatch(clearErrors());
    this.store.dispatch(
      AuthActions.setBookingRef({
        lastName: event.lastName,
        recLoc: event.recLoc,
      })
    );
    this.store.dispatch(AuthActions.loadPNR());
  }
}
